/**
 * Yogi Point and its planets (Seshadri Iyer method).
 *
 * Mirrors calculate_yogi_avayogi() in the chart-calculator lambda. Computed on
 * the client rather than read from chartData.yogi_avayogi because charts are
 * cached in localStorage: any chart generated before the 93°20' constant was
 * added carries a Yogi Point seven nakshatras off, and would otherwise keep
 * showing the wrong planets until the user regenerated it.
 */

const NAKSHATRA_LORDS = [
  'Ketu', 'Venus', 'Sun', 'Moon', 'Mars', 'Rahu', 'Jupiter', 'Saturn', 'Mercury',
];

const NAKSHATRA_NAMES = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashirsa', 'Ardra',
  'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni',
  'Uttara Phalguni', 'Hasta', 'Chitra', 'Swati', 'Vishika', 'Anuradha',
  'Jyestha', 'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana',
  'Dhanishta', 'Shatabhisha', 'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati',
];

const SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
];

const SIGN_LORDS = {
  Aries: 'Mars', Taurus: 'Venus', Gemini: 'Mercury', Cancer: 'Moon',
  Leo: 'Sun', Virgo: 'Mercury', Libra: 'Venus', Scorpio: 'Mars',
  Sagittarius: 'Jupiter', Capricorn: 'Saturn', Aquarius: 'Saturn',
  Pisces: 'Jupiter',
};

// 3 signs, 3 degrees, 20 minutes
const YOGI_CONSTANT = 93 + 20 / 60;
const NAKSHATRA_SPAN = 360 / 27;

/**
 * @returns {{birthYogi, duplicateYogi, avayogi, ...}|null} null when the chart
 * has no usable Sun/Moon longitudes.
 */
export function resolveYogiAvayogi(chartData) {
  const sun = Number(chartData?.planets?.Sun?.longitude);
  const moon = Number(chartData?.planets?.Moon?.longitude);
  if (!Number.isFinite(sun) || !Number.isFinite(moon)) return null;

  const point = (((sun + moon + YOGI_CONSTANT) % 360) + 360) % 360;
  const nak = Math.floor(point / NAKSHATRA_SPAN) % 27;
  const sign = SIGNS[Math.floor(point / 30) % 12];

  // Avayogi: the 6th nakshatra counting the Yogi's own as 1.
  const avayogiNak = (nak + 5) % 27;

  return {
    longitude: point,
    sign,
    degree: point % 30,
    nakshatra: NAKSHATRA_NAMES[nak],
    birthYogi: NAKSHATRA_LORDS[nak % 9],
    duplicateYogi: SIGN_LORDS[sign],
    avayogi: NAKSHATRA_LORDS[avayogiNak % 9],
    avayogiNakshatra: NAKSHATRA_NAMES[avayogiNak],
  };
}
